"use client";

import { productHref } from "@/shared/routes";
import { RiCloseLine, RiSearchLine } from "react-icons/ri";
import { SearchChatCta } from "./SearchChatCta";
import { SearchProductCard } from "./SearchProductCard";
import { ProductCard, normalizeVariant, cardOrientation } from "@/modules/catalog";
import type { ProductCardModel } from "@/modules/catalog";
import { useTheme } from "@/theme";
import type { SearchResult, VisitedProduct } from "../types/search";
import { EmptyState, Text } from "@dionatanbig2be/big2be-ui";

interface SearchSuggestionsProps {
    /** Oculta a vitrine de produtos visitados (mostra só histórico). */
    compact?: boolean;
    /** Renderiza em fluxo normal em vez de overlay absoluto (uso dentro de um modal fullscreen). */
    staticPosition?: boolean;
    query: string;
    suggestions: string[];
    /** Resultados em tempo real; null = ainda não buscou. */
    searchResults: SearchResult[] | null;
    isSearching: boolean;
    searchHistory: string[];
    visitedProducts: VisitedProduct[];
    onSelectTerm: (term: string) => void;
    onRemoveHistory: (term: string) => void;
    onSelectVisited: (product: VisitedProduct) => void;
    /** Chamado quando o CTA "pergunte ao assistente" é acionado (ex.: fechar o
     * dropdown antes de o chat abrir). Sem ele o CTA não é exibido. */
    onAskChat?: () => void;
}

const ROW =
    "flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-black transition-colors hover:bg-black/5";
function toSearchProductCardModel(result: SearchResult): ProductCardModel {
    return {
        productId: result.id,
        slug: result.slug,
        href: productHref({ slug: result.slug, productId: result.id }),
        name: result.name,
        brand: result.brand,
        image: result.imageUrl ?? null,
        extraImages: 0,
        price: result.price,
        oldPrice: result.oldPrice,
        currency: "BRL",
        inStock: result.hasStock,
        saleRules: result.saleRules,
    };
}

/**
 * Dropdown da barra de busca. Digitando → loading + sugestões + resultados +
 * empty. Idle → vitrine de visitados (se houver) + histórico.
 */
export function SearchSuggestions({
    compact = false,
    staticPosition = false,
    query,
    suggestions,
    searchResults,
    isSearching,
    searchHistory,
    visitedProducts,
    onSelectTerm,
    onRemoveHistory,
    onSelectVisited,
    onAskChat,
}: SearchSuggestionsProps) {
    const isTyping = query.trim().length > 0;
    // "variant1" (Sugestão de busca + Produtos) é o default: identidades
    // antigas, salvas antes deste campo existir, não têm `template` nenhum.
    const template = useTheme()?.components?.search?.template ?? "variant1";

    if (isTyping) {
        return (
            <Panel
                staticPosition={staticPosition}
                footer={onAskChat ? <SearchChatCta term={query.trim()} onActivate={onAskChat} /> : undefined}
            >
                <TypingResults
                    template={template}
                    query={query}
                    suggestions={suggestions}
                    searchResults={searchResults}
                    isSearching={isSearching}
                    searchHistory={searchHistory}
                    onSelectTerm={onSelectTerm}
                    onRemoveHistory={onRemoveHistory}
                />
            </Panel>
        );
    }

    const hasIdleContent = searchHistory.length > 0 || (!compact && visitedProducts.length > 0);

    if (!hasIdleContent) {
        return (
            <Panel staticPosition={staticPosition}>
                <IdleEmptyState />
            </Panel>
        );
    }

    return (
        <Panel>
            {!compact && visitedProducts.length > 0 && (
                <VisitedShowcase products={visitedProducts} onSelect={onSelectVisited} />
            )}
            {searchHistory.length > 0 && (
                <SearchHistoryList
                    terms={searchHistory}
                    onSelectTerm={onSelectTerm}
                    onRemoveTerm={onRemoveHistory}
                />
            )}
        </Panel>
    );
}

// Fundo/texto fixos (branco/preto) — padrão comum de dropdown de busca,
// independente do tema (claro/escuro) do resto do site.
//
// IMPORTANTE — `--color-primary` aqui NÃO é a primary de verdade: é um
// empréstimo do `--color-accent` (nome da variável CSS tem que continuar
// sendo `--color-primary` porque é isso que `text-primary`/`bg-primary`/
// `border-primary` do Tailwind/daisyUI leem — não dá pra renomear). Motivo:
// o tema do header (Identidade Visual → Tema do Cabeçalho) configura uma
// `primary` PRÓPRIA (branca nesta loja, usada de propósito pra contrastar
// ícones do header) que fica ilegível no fundo branco fixo do painel. Como
// o `ProductCard` (componente compartilhado) usa `primary` internamente pro
// preço/borda do carrinho, redefinir só AQUI DENTRO (escopo do painel de
// busca) resolve sem mexer no componente nem afetar outras telas que o usam.
const BORROWED_ACCENT_AS_PRIMARY = {
    "--color-primary": "var(--color-accent)",
    "--color-primary-content": "var(--color-accent-content)",
} as React.CSSProperties;

/** Exportado para o modal fullscreen mobile aplicar o mesmo truque — ver `SearchFullscreenModal.tsx`. */
export const FIXED_SURFACE_VARS = {
    "--color-base-100": "#ffffff",
    "--color-base-200": "#f5f5f5",
    "--color-base-300": "#e5e5e5",
    "--color-base-content": "#000000",
    ...BORROWED_ACCENT_AS_PRIMARY,
} as React.CSSProperties;

function Panel({
    children,
    footer,
    staticPosition,
}: {
    children: React.ReactNode;
    /** Rodapé fixo, fora da área de rolagem — mesma moldura do `SideDrawer` (carrinho/busca mobile). */
    footer?: React.ReactNode;
    staticPosition?: boolean;
}) {
    return (
        <div
            style={FIXED_SURFACE_VARS}
            className={
                staticPosition
                    ? "@container flex min-h-full flex-1 flex-col overflow-hidden"
                    : "@container absolute left-0 right-0 top-full mt-3 z-50 flex max-h-[70vh] flex-col overflow-hidden rounded-lg border border-base-300 bg-white shadow-lg"
            }
        >
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">{children}</div>
            {footer && (
                <div className="flex shrink-0 flex-col border-t-2 border-base-content/15 bg-base-200 pt-2.5">
                    <p className="px-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-base-content/40">
                        Não encontrou o que procurava?
                    </p>
                    {footer}
                </div>
            )}
        </div>
    );
}

/** Remove acentos para comparação — "racao" deve casar com "Ração". */
const DIACRITICS_PATTERN = /[̀-ͯ]/g;

function normalizeForMatch(text: string): string {
    return text.normalize("NFD").replace(DIACRITICS_PATTERN, "").toLowerCase();
}

/** Destaca o trecho que casa com a query dentro de um texto (ignora acento na comparação, mantém o texto original no destaque). */
function Highlight({ text, query }: { text: string; query: string }) {
    const idx = normalizeForMatch(text).indexOf(normalizeForMatch(query));
    if (idx < 0 || !query) return <span>{text}</span>;
    return (
        <span>
            {text.slice(0, idx)}
            <strong className="text-primary">{text.slice(idx, idx + query.length)}</strong>
            {text.slice(idx + query.length)}
        </span>
    );
}

/**
 * O que a busca lista enquanto a pessoa digita — configurável em Identidade
 * Visual → Vitrine → Modelo da tela de busca:
 *   - variant1 "Sugestão de busca + Produtos": termo sugerido + grade (padrão).
 *   - variant2 "Histórico de busca + Produtos": buscas anteriores em vez do
 *     termo sugerido, + grade.
 *   - variant3 "Só produtos": só a grade, sem sugestão nem histórico.
 */
function TypingResults({
    template,
    query,
    suggestions,
    searchResults,
    isSearching,
    searchHistory,
    onSelectTerm,
    onRemoveHistory,
}: {
    template: string;
    query: string;
    suggestions: string[];
    searchResults: SearchResult[] | null;
    isSearching: boolean;
    searchHistory: string[];
    onSelectTerm: (term: string) => void;
    onRemoveHistory: (term: string) => void;
}) {
    const hasResults = !isSearching && searchResults && searchResults.length > 0;
    const noResults = !isSearching && searchResults !== null && searchResults.length === 0;

    return (
        <>
            {isSearching && <SearchingIndicator />}
            {template === "variant2" && searchHistory.length > 0 && (
                <SearchHistoryList
                    terms={searchHistory}
                    onSelectTerm={onSelectTerm}
                    onRemoveTerm={onRemoveHistory}
                />
            )}
            {template !== "variant2" && template !== "variant3" && suggestions.length > 0 && (
                <TermSuggestionList terms={suggestions} query={query} onSelectTerm={onSelectTerm} />
            )}
            {noResults && <NoResultsMessage query={query} />}
            {hasResults && <ProductResultsGrid results={searchResults!} query={query} />}
        </>
    );
}

function SearchingIndicator() {
    return (
        <div className="flex justify-center py-3">
            <span className="loading loading-dots loading-xs text-primary" />
        </div>
    );
}

function TermSuggestionList({
    terms,
    query,
    onSelectTerm,
}: {
    terms: string[];
    query: string;
    onSelectTerm: (term: string) => void;
}) {
    return (
        <ul role="listbox" aria-label="Sugestões">
            {terms.map((term, index) => (
                <li key={`${term}-${index}`} role="option" aria-selected="false">
                    <button type="button" className={ROW} onClick={() => onSelectTerm(term)}>
                        <RiSearchLine size={14} className="shrink-0 text-black/40" />
                        <Highlight text={term} query={query} />
                    </button>
                </li>
            ))}
        </ul>
    );
}

function NoResultsMessage({ query }: { query: string }) {
    return (
        <div className="min-w-0 px-4 py-6 text-center text-black">
            {/* [overflow-wrap:anywhere]: termo longo sem espaços quebra em
                qualquer ponto em vez de estourar a largura do painel. */}
            <p className="text-sm [overflow-wrap:anywhere]">
                Nenhum resultado para <strong className="text-primary">“{query}”</strong>
            </p>
            <p className="text-sm text-black/50">Tente buscar de outro jeito</p>
        </div>
    );
}

function ProductResultsGrid({ results, query }: { results: SearchResult[]; query: string }) {
    const theme = useTheme();
    // A lista de sugestões é uma coluna estreita: só card deitado cabe. O
    // template do portal vale quando já é deitado; senão, o padrão horizontal.
    const themeCardVariant = normalizeVariant(theme?.components?.card?.template);
    const cardVariant =
        cardOrientation(themeCardVariant) === "horizontal" ? themeCardVariant : "variant-2";

    return (
        <>
            <div className="bg-transparent w-full h-2"></div>
            <Text className="px-2 text-sm text-black/60">
                {results.length === 1
                    ? `1 resultado encontrado com “${query}”.`
                    : `${results.length} resultados encontrados com “${query}”.`}
            </Text>
            <ul
                role="listbox"
                className="grid min-w-0 grid-cols-1 gap-2 px-2 py-3 @min-[680px]:grid-cols-2 @min-[68rem]:grid-cols-3"
            >
                {/* id pode repetir (mesmo produto em variações) → key composta. */}
                {results.map((result, i) => (
                    <li
                        key={`${result.id}-${i}`}
                        role="option"
                        aria-selected="false"
                        className="rounded-box border-2 border-gray-300"
                    >
                        <ProductCard
                            disableQuantityStepper
                            product={toSearchProductCardModel(result)}
                            variant={cardVariant}
                            block
                        />
                    </li>
                ))}
            </ul>
        </>
    );
}

function IdleEmptyState() {
    return (
        <EmptyState
            icon={<RiSearchLine size={22} />}
            title="Comece a digitar para buscar"
            description="Os produtos que você procurar vão aparecer aqui."
        />
    );
}

function VisitedShowcase({
    products,
    onSelect,
}: {
    products: VisitedProduct[];
    onSelect: (product: VisitedProduct) => void;
}) {
    return (
        <section className="p-3">
            <h4 className="mb-2 px-1 text-xs font-semibold text-black/60">Sugestões</h4>
            <div className="flex gap-3 overflow-x-auto pb-1">
                {products.map((product) => (
                    <SearchProductCard key={product.id} product={product} onClick={onSelect} />
                ))}
            </div>
        </section>
    );
}

function SearchHistoryList({
    terms,
    onSelectTerm,
    onRemoveTerm,
}: {
    terms: string[];
    onSelectTerm: (term: string) => void;
    onRemoveTerm: (term: string) => void;
}) {
    return (
        <ul role="listbox">
            {terms.map((term) => (
                <li key={term} role="option" aria-selected="false" className="flex items-center">
                    <button
                        type="button"
                        className={`${ROW} flex-1`}
                        onClick={() => onSelectTerm(term)}
                    >
                        {term}
                    </button>
                    <button
                        type="button"
                        aria-label={`Remover "${term}" do histórico`}
                        onClick={() => onRemoveTerm(term)}
                        className="p-2 text-black/40 transition-colors hover:text-black"
                    >
                        <RiCloseLine size={16} />
                    </button>
                </li>
            ))}
        </ul>
    );
}
